import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import './GrantDocuments.css';
import { NOTICE_OF_AWARD_DOC_ID } from '../utils/grantDocumentIds';

// Notices of Award for a funded grant.
//
// Unlike the application and Just-in-Time checklists there is no fixed list here:
// an agency issues one NOA up front and often another each budget year, so this
// page is a growing list of uploads that all share one document ID. Each record
// carries a label (usually the award year) so the list stays readable.

const GrantNoticeOfAward = ({ userInfo }) => {
  const { applicationId } = useParams();
  const navigate = useNavigate();

  const [application, setApplication] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [notices, setNotices] = useState([]);
  const [newNoticeLabel, setNewNoticeLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  // Replacing a specific notice: upload the new file, then drop the old record.
  const replaceInputRef = useRef(null);
  const [replacingNotice, setReplacingNotice] = useState(null);

  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [savingLabel, setSavingLabel] = useState(false);

  const isXlsxFile = (filename) => {
    if (!filename) return false;
    const extension = filename.toLowerCase().split('.').pop();
    return extension === 'xlsx' || extension === 'xls';
  };

  const parseMetadata = (doc) => {
    try {
      if (!doc.metadata) return {};
      return typeof doc.metadata === 'string' ? JSON.parse(doc.metadata) : doc.metadata;
    } catch (parseError) {
      return {};
    }
  };

  const displayFilename = (filename) => (filename || '').replace(/^NOA_\d+_/, '');

  const toNotice = (doc) => {
    const metadata = parseMetadata(doc);
    return {
      id: doc.id,
      filename: doc.filename,
      uploadDate: doc.uploadDate,
      fileUrl: doc.fileUrl,
      label: metadata.label || ''
    };
  };

  const fetchApplicationDetails = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}`,
        { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch application details');
      }

      const data = await response.json();
      setApplication(data.application);

      const documents = data.application.documents || [];
      setNotices(
        documents
          .filter(doc => String(doc.documentId) === String(NOTICE_OF_AWARD_DOC_ID))
          .map(toNotice)
          .sort((a, b) => new Date(b.uploadDate || 0) - new Date(a.uploadDate || 0))
      );
    } catch (fetchError) {
      console.error('Error fetching application:', fetchError);
      setError('Failed to load application details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (applicationId) {
      fetchApplicationDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const handleFileDownload = async (fileUrl, filename) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Download failed:', downloadError);
      window.open(fileUrl, '_blank');
    }
  };

  // Same signed-URL upload path the other grant document pages use.
  const uploadToApplication = async (file, filename, metadata) => {
    const getUrlResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/document-upload-url`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId: NOTICE_OF_AWARD_DOC_ID, filename, contentType: file.type })
      }
    );

    if (!getUrlResponse.ok) {
      throw new Error('Failed to get upload URL');
    }

    const urlData = await getUrlResponse.json();

    const uploadResponse = await fetch(urlData.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload document to cloud storage');
    }

    const confirmResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/confirm-document-upload`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId: NOTICE_OF_AWARD_DOC_ID,
          filename,
          destination: urlData.destination,
          downloadUrl: urlData.downloadUrl,
          ...(metadata ? { metadata } : {})
        })
      }
    );

    if (!confirmResponse.ok) {
      throw new Error('Failed to confirm document upload');
    }

    const confirmData = await confirmResponse.json();
    return { document: confirmData.document, downloadUrl: urlData.downloadUrl };
  };

  const deleteNoticeRecord = async (documentRecordId) => {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/documents/${documentRecordId}`,
      { method: 'DELETE', credentials: 'include', headers: { 'Content-Type': 'application/json' } }
    );
    if (!response.ok) {
      throw new Error('Failed to delete document');
    }
  };

  const handleUpload = async (file, label) => {
    if (!file) return;

    setUploading(true);
    try {
      const uniqueFilename = `NOA_${Date.now()}_${file.name}`;
      const { document: created, downloadUrl } = await uploadToApplication(
        file,
        uniqueFilename,
        { label: label || '', originalFilename: file.name }
      );

      setNotices(prev => [
        {
          id: created?.id || `${Date.now()}`,
          filename: uniqueFilename,
          uploadDate: new Date().toISOString(),
          fileUrl: downloadUrl,
          label: label || ''
        },
        ...prev
      ]);
      setNewNoticeLabel('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (uploadError) {
      console.error('Error uploading Notice of Award:', uploadError);
      alert(`Failed to upload Notice of Award: ${uploadError.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleReplace = async (notice, file) => {
    if (!file) return;

    setUploading(true);
    try {
      const uniqueFilename = `NOA_${Date.now()}_${file.name}`;
      const { document: created, downloadUrl } = await uploadToApplication(
        file,
        uniqueFilename,
        { label: notice.label || '', originalFilename: file.name }
      );

      // The replacement is on file before the old record goes, so a failure part
      // way through never leaves the grant without a Notice of Award.
      await deleteNoticeRecord(notice.id);

      setNotices(prev => [
        {
          id: created?.id || `${Date.now()}`,
          filename: uniqueFilename,
          uploadDate: new Date().toISOString(),
          fileUrl: downloadUrl,
          label: notice.label || ''
        },
        ...prev.filter(n => n.id !== notice.id)
      ]);
    } catch (replaceError) {
      console.error('Error replacing Notice of Award:', replaceError);
      alert(`Failed to replace Notice of Award: ${replaceError.message}`);
    } finally {
      setUploading(false);
      setReplacingNotice(null);
      if (replaceInputRef.current) replaceInputRef.current.value = '';
    }
  };

  const handleSaveLabel = async (noticeId) => {
    setSavingLabel(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/documents/${noticeId}/metadata`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ metadata: { label: editingLabel } })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save label');
      }

      setNotices(prev => prev.map(n => (n.id === noticeId ? { ...n, label: editingLabel } : n)));
      setEditingLabelId(null);
      setEditingLabel('');
    } catch (saveError) {
      console.error('Error updating Notice of Award label:', saveError);
      alert(`Failed to save label: ${saveError.message}`);
    } finally {
      setSavingLabel(false);
    }
  };

  const handleRemove = async (notice) => {
    if (!window.confirm(`Remove "${displayFilename(notice.filename)}"?`)) return;
    try {
      await deleteNoticeRecord(notice.id);
      setNotices(prev => prev.filter(n => n.id !== notice.id));
    } catch (removeError) {
      console.error('Error removing Notice of Award:', removeError);
      alert(`Failed to remove document: ${removeError.message}`);
    }
  };

  const handleBatchDownload = async () => {
    const files = notices.filter(n => n.fileUrl);
    if (files.length === 0) {
      alert('No Notices of Award have been uploaded yet.');
      return;
    }

    const button = document.querySelector('.batch-download-btn');
    const originalText = button?.textContent;

    try {
      const zip = new JSZip();
      if (button) {
        button.textContent = 'Creating zip file...';
        button.disabled = true;
      }

      for (const file of files) {
        try {
          const response = await fetch(file.fileUrl);
          if (response.ok) {
            zip.file(displayFilename(file.filename) || 'notice-of-award', await response.blob());
          }
        } catch (fileError) {
          console.error(`Failed to fetch ${file.filename}:`, fileError);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `notices_of_award_${applicationId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (zipError) {
      console.error('Error creating zip file:', zipError);
      alert('Failed to create zip file. Please try again.');
    } finally {
      if (button) {
        button.textContent = originalText;
        button.disabled = false;
      }
    }
  };

  if (isLoading) {
    return <div className="loading-indicator">Loading Notices of Award...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="grant-documents-container">
      <div className="documents-header">
        <h1>Notice of Award Documents</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="return-btn"
            onClick={() => navigate('/grant-fund-financials?tab=grants')}
          >
            Grant Report
          </button>
          <button className="return-btn" onClick={() => navigate('/grant-submission')}>
            Return to Applications
          </button>
        </div>
      </div>

      <div className="application-info-bar">
        <h2>{application?.name}</h2>
        <div className="document-progress">
          <span className="progress-text">
            {notices.length} {notices.length === 1 ? 'Notice' : 'Notices'} of Award on file
          </span>
        </div>
        <div className="batch-download-section">
          <button
            className="batch-download-btn"
            onClick={handleBatchDownload}
            disabled={notices.length === 0}
            title={notices.length === 0 ? 'Nothing uploaded yet' : 'Download every Notice of Award'}
          >
            Download All ({notices.length})
          </button>
        </div>
      </div>

      <div className="documents-list">
        <div className="document-card">
          <div className="document-header">
            <div className="document-title-block">
              <h4>Notices of Award</h4>
              <span className="document-meta">
                Agencies often issue a new notice each budget year — keep every one here.
              </span>
            </div>
            <div className="document-header-actions">
              <span className={`status-indicator ${notices.length > 0 ? 'uploaded' : 'missing'}`}>
                {notices.length > 0 ? 'Uploaded' : 'Required'}
              </span>
            </div>
          </div>

          <div className="biosketches-section">
            {notices.length > 0 && (
              <div className="biosketches-list">
                <h5>Uploaded Notices</h5>
                {notices.map(notice => (
                  <div key={notice.id} className="biosketch-item">
                    <div className="biosketch-info">
                      {editingLabelId === notice.id ? (
                        <input
                          type="text"
                          className="note-editor"
                          value={editingLabel}
                          onChange={(e) => setEditingLabel(e.target.value)}
                          placeholder="e.g. Year 1 / 2026"
                        />
                      ) : (
                        <span className="personnel-name">{notice.label || 'Notice of Award'}</span>
                      )}
                      <span className="biosketch-meta">
                        {[
                          displayFilename(notice.filename),
                          notice.uploadDate ? new Date(notice.uploadDate).toLocaleDateString() : null
                        ].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <div className="biosketch-actions">
                      {editingLabelId === notice.id ? (
                        <>
                          <button
                            className="view-btn small"
                            disabled={savingLabel}
                            onClick={() => handleSaveLabel(notice.id)}
                          >
                            {savingLabel ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            className="replace-btn small"
                            disabled={savingLabel}
                            onClick={() => {
                              setEditingLabelId(null);
                              setEditingLabel('');
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="edit-btn small"
                            onClick={() => {
                              setEditingLabelId(notice.id);
                              setEditingLabel(notice.label || '');
                            }}
                          >
                            Edit Label
                          </button>
                          {notice.fileUrl && (
                            <>
                              {!isXlsxFile(notice.filename) && (
                                <button
                                  className="view-btn small"
                                  onClick={() => window.open(notice.fileUrl, '_blank')}
                                >
                                  View
                                </button>
                              )}
                              <button
                                className="download-btn small"
                                onClick={() => handleFileDownload(notice.fileUrl, displayFilename(notice.filename))}
                              >
                                Download
                              </button>
                            </>
                          )}
                          <button
                            className="replace-btn small"
                            disabled={uploading}
                            onClick={() => {
                              setReplacingNotice(notice);
                              replaceInputRef.current?.click();
                            }}
                          >
                            Replace
                          </button>
                          <button
                            className="remove-btn small"
                            onClick={() => handleRemove(notice)}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="additional-doc-upload-box">
              <div className="additional-doc-upload-row">
                <span className="additional-doc-upload-label">Add a Notice of Award</span>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUpload(file, newNoticeLabel);
                  }}
                />
                <button
                  className="add-biosketch-btn"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? 'Uploading...' : '+ Upload'}
                </button>
              </div>
              <div style={{ marginTop: '10px' }}>
                <label
                  htmlFor="noa-label"
                  style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}
                >
                  Label:
                </label>
                <input
                  id="noa-label"
                  type="text"
                  value={newNoticeLabel}
                  onChange={(e) => setNewNoticeLabel(e.target.value)}
                  placeholder="e.g. Year 1 / 2026 — set this before choosing the file"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {/* Shared input for "Replace" on any one notice. */}
            <input
              type="file"
              ref={replaceInputRef}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && replacingNotice) handleReplace(replacingNotice, file);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrantNoticeOfAward;
